import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Label } from "~/components/ui";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { useCreateProfitMarginMutation } from "~/hooks/profit-margins/useCreateProfitMarginMutation";
import { useProfitMargins } from "~/hooks/profit-margins/useProfitMargins";
import { useUpdateProfitMarginMutation } from "~/hooks/profit-margins/useUpdateProfitMarginMutation";
import {
  profitMarginSchema,
  type ProfitMarginFormData,
} from "~/schema/profit-margin";

export function MargensLucroContent() {
  const { profitMargins, isLoading } = useProfitMargins();
  const { createProfitMargin, isPending: isCreating } =
    useCreateProfitMarginMutation();
  const { updateProfitMargin, isPending: isUpdating } =
    useUpdateProfitMarginMutation();

  const activeProfitMargin = profitMargins.find((pm) => pm.isActive);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProfitMarginFormData>({
    resolver: zodResolver(profitMarginSchema),
    defaultValues: {
      cardPercentage: 0,
      lossPercentage: 0,
      cardTaxPercentage: 0,
      breakagePercentage: 0,
      packagingPercentage: 0,
      expectedTaxPercentage: undefined,
      ownManufacturingSharePercentage: undefined,
      resaleProductsSharePercentage: undefined,
      expectedNetMarginPercentage: undefined,
      averageResaleProductsMarginPercentage: undefined,
    },
  });

  useEffect(() => {
    if (activeProfitMargin) {
      reset({
        cardPercentage: activeProfitMargin.cardPercentage,
        lossPercentage: activeProfitMargin.lossPercentage,
        cardTaxPercentage: activeProfitMargin.cardTaxPercentage,
        breakagePercentage: activeProfitMargin.breakagePercentage,
        packagingPercentage: activeProfitMargin.packagingPercentage,
        expectedTaxPercentage:
          activeProfitMargin.expectedTaxPercentage ?? undefined,
        ownManufacturingSharePercentage:
          activeProfitMargin.ownManufacturingSharePercentage ?? undefined,
        resaleProductsSharePercentage:
          activeProfitMargin.resaleProductsSharePercentage ?? undefined,
        expectedNetMarginPercentage:
          activeProfitMargin.expectedNetMarginPercentage ?? undefined,
        averageResaleProductsMarginPercentage:
          activeProfitMargin.averageResaleProductsMarginPercentage ?? undefined,
      });
    }
  }, [activeProfitMargin, reset]);

  const onSubmit = async (data: ProfitMarginFormData) => {
    try {
      if (activeProfitMargin) {
        await updateProfitMargin({
          id: activeProfitMargin.id,
          data,
        });
      } else {
        await createProfitMargin(data);
      }
    } catch {
      toast.error("Erro ao salvar configuração");
    }
  };

  const isPending = isCreating || isUpdating;

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="space-y-2">
            <Label htmlFor="cardPercentage">
              Percentual de vendas no Cartão (%)
            </Label>
            <Input
              id="cardPercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register("cardPercentage", { valueAsNumber: true })}
              placeholder="0.00"
            />
            {errors.cardPercentage && (
              <p className="text-red-600 text-sm">
                {errors.cardPercentage.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="lossPercentage">Percentual de perdas (%)</Label>
            <Input
              id="lossPercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register("lossPercentage", { valueAsNumber: true })}
              placeholder="0.00"
            />
            {errors.lossPercentage && (
              <p className="text-red-600 text-sm">
                {errors.lossPercentage.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="cardTaxPercentage">
              Percentual pago de taxa de Cartão (%)
            </Label>
            <Input
              id="cardTaxPercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register("cardTaxPercentage", { valueAsNumber: true })}
              placeholder="0.00"
            />
            {errors.cardTaxPercentage && (
              <p className="text-red-600 text-sm">
                {errors.cardTaxPercentage.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="breakagePercentage">
              Percentual de Quebra (%)
            </Label>
            <Input
              id="breakagePercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register("breakagePercentage", { valueAsNumber: true })}
              placeholder="0.00"
            />
            {errors.breakagePercentage && (
              <p className="text-red-600 text-sm">
                {errors.breakagePercentage.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="packagingPercentage">
              Percentual pago para embalagens (%)
            </Label>
            <Input
              id="packagingPercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register("packagingPercentage", { valueAsNumber: true })}
              placeholder="0.00"
            />
            {errors.packagingPercentage && (
              <p className="text-red-600 text-sm">
                {errors.packagingPercentage.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="expectedTaxPercentage">
              Imposto previsto (%)
            </Label>
            <Input
              id="expectedTaxPercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register("expectedTaxPercentage", {
                setValueAs: (v) =>
                  v === "" || Number.isNaN(Number(v)) ? undefined : Number(v),
              })}
              placeholder="0.00"
            />
            {errors.expectedTaxPercentage && (
              <p className="text-red-600 text-sm">
                {errors.expectedTaxPercentage.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="ownManufacturingSharePercentage">
              Participação Fabricação Própria (%)
            </Label>
            <Input
              id="ownManufacturingSharePercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register("ownManufacturingSharePercentage", {
                setValueAs: (v) =>
                  v === "" || Number.isNaN(Number(v)) ? undefined : Number(v),
              })}
              placeholder="0.00"
            />
            {errors.ownManufacturingSharePercentage && (
              <p className="text-red-600 text-sm">
                {errors.ownManufacturingSharePercentage.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="resaleProductsSharePercentage">
              Participação produtos de revenda (%)
            </Label>
            <Input
              id="resaleProductsSharePercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register("resaleProductsSharePercentage", {
                setValueAs: (v) =>
                  v === "" || Number.isNaN(Number(v)) ? undefined : Number(v),
              })}
              placeholder="0.00"
            />
            {errors.resaleProductsSharePercentage && (
              <p className="text-red-600 text-sm">
                {errors.resaleProductsSharePercentage.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="expectedNetMarginPercentage">
              Margem Líquida Prevista (%)
            </Label>
            <Input
              id="expectedNetMarginPercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register("expectedNetMarginPercentage", {
                setValueAs: (v) =>
                  v === "" || Number.isNaN(Number(v)) ? undefined : Number(v),
              })}
              placeholder="0.00"
            />
            {errors.expectedNetMarginPercentage && (
              <p className="text-red-600 text-sm">
                {errors.expectedNetMarginPercentage.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="averageResaleProductsMarginPercentage">
              Margem Média dos produtos de Revenda (%)
            </Label>
            <Input
              id="averageResaleProductsMarginPercentage"
              type="number"
              step="0.01"
              min="0"
              max="100"
              {...register("averageResaleProductsMarginPercentage", {
                setValueAs: (v) =>
                  v === "" || Number.isNaN(Number(v)) ? undefined : Number(v),
              })}
              placeholder="0.00"
            />
            {errors.averageResaleProductsMarginPercentage && (
              <p className="text-red-600 text-sm">
                {errors.averageResaleProductsMarginPercentage.message}
              </p>
            )}
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button
            type="submit"
            disabled={isPending}
            className="min-w-[120px]"
          >
            {isPending ? "Salvando..." : "Salvar Configuração"}
          </Button>
        </div>
      </div>
    </form>
  );
}
